import { useState, type ComponentPropsWithoutRef } from "react";

import {
  PlanningScope,
  PlanningType,
  RoomType,
  type Building,
  type HeatingNetwork,
  type Planning,
  type PutPlanningBody,
} from "@eisox/api/core-api";
import { Button, CanAccess, Card, cn, Heading } from "@eisox/design-system";
import { PlusIcon } from "@eisox/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { planningApi, roomApi } from "~/api";
import {
  FrostFreeSource,
  getRoomFrostFreePlanned,
  getRoomFrostFreeStatus,
  RoomFrostFreeAlert,
  type FrostFreeRoomResult,
  type RoomFrostFreeSlots,
} from "~/features/frost-free";
import {
  mergeTimeslot,
  OptionsDropdown,
  organizePlanningsByWeekDays,
  PlanningTypeKey,
  UpdatePlanningDialog,
  Week,
  type CopyToDaysSchemaType,
} from "~/features/planning";
import { m } from "~/paraglide/messages";
import type { Permission } from "~/types/auth.type";
import type { EnhancedRoom } from "~/types/room.type";
import type { EnhancedValve } from "~/types/valve.type";

import { canAccessFrostFreeSettings, canAccessPlanning, copyToDaysSchemaTypeToPutPlanningBody } from "../helpers";
import { CopyPlanningDialog } from "./copy-planning-dialog";
import { Settings } from "./settings";

const NAME = "PlanningCard";

/** Dismissible so a partial network frost free does not permanently hide the planning. */
const FrostFreePlanningOverlay: React.FC<{
  result: FrostFreeRoomResult;
  slots: RoomFrostFreeSlots;
  heatingNetworks: HeatingNetwork[];
}> = ({ result, slots, heatingNetworks }) => {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  return (
    <div className="absolute top-1/2 left-1/2 flex h-full w-full -translate-x-1/2 -translate-y-1/2 items-center justify-center backdrop-blur-[1.7px]">
      <RoomFrostFreeAlert.Active
        result={result}
        slots={slots}
        heatingNetworks={heatingNetworks}
        onClose={() => setDismissed(true)}
      />
    </div>
  );
};

interface PlanningCardProps extends ComponentPropsWithoutRef<"div"> {
  building: Building;
  room: EnhancedRoom;
  plannings: Planning[];
  valves: EnhancedValve[];
  heatingNetworks: HeatingNetwork[];
  permissions: Permission;
}

const PlanningCard: React.FC<PlanningCardProps> = ({
  building,
  room,
  plannings,
  valves,
  heatingNetworks,
  className,
  permissions,
  ...props
}) => {
  const { comfortTemperature, nightTemperature, type } = room;
  const { absenceTemperature, preComfortTemperature } = building;

  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: (body: PutPlanningBody) => planningApi.update(building.id, body),
    onSuccess: async (_, request) => {
      await Promise.all(
        request.parentIds.map(roomId =>
          queryClient.invalidateQueries({ queryKey: roomApi.queries.plannings(roomId).queryKey }),
        ),
      );
    },
  });

  const networksOfRoom = heatingNetworks.filter(network =>
    valves.some(valve => valve.roomId === room.id && valve.heatingNetworkId === network.id),
  );

  const plannedFrostFree = getRoomFrostFreePlanned({
    buildingFrostFree: building.frostFree,
    roomFrostFree: room.frostFree,
    networksOfRoom,
  });
  const frostFreeResult = getRoomFrostFreeStatus({
    buildingFrostFree: building.frostFree,
    roomFrostFree: room.frostFree,
    networksOfRoom,
  });
  const isFrostFree = frostFreeResult.active;

  const frostFreeSlots: RoomFrostFreeSlots = {
    [FrostFreeSource.BUILDING]: permissions.building?.read
      ? alert => (
          <Link
            to="/buildings/$buildingId/settings/buildings/holidays"
            params={{ buildingId: building.id }}
            style={{ display: "contents" }}
          >
            {alert}
          </Link>
        )
      : null,
    [FrostFreeSource.HEATING_NETWORK]: null,
    [FrostFreeSource.HEATING_NETWORK_PARTIAL]: null,
    [FrostFreeSource.ROOM]: canAccessFrostFreeSettings(permissions)
      ? alert => <Settings value="frostFree">{alert}</Settings>
      : null,
  };

  const [copyPlanningDialogOpen, setCopyPlanningDialogOpen] = useState(false);
  const [displayUserDefinedPlanning, setDisplayUserDefinedPlanning] = useState(true);

  const types: PlanningType[] = [
    PlanningType.ABSENCE,
    PlanningType.COMFORT,
    ...(type === RoomType.ROOM ? ([PlanningType.NIGHT] as const) : []),
  ];

  const userDefinedPlannings = plannings.filter(planning => planning.userDefined);
  const generatedPlannings = plannings.filter(planning => !planning.userDefined);
  const mergedPlannings = displayUserDefinedPlanning ? mergeTimeslot(plannings) : generatedPlannings;

  const { reorderedDays, timeslotsByDay } = organizePlanningsByWeekDays(mergedPlannings);

  const hasPlanningAccess = canAccessPlanning(permissions);

  const handleChangePlanning = async (plannings: Planning[]) => {
    await mutateAsync({ scope: PlanningScope.ROOM, parentIds: [room.id], plannings });
  };

  const handleCopyPlanning = async (data: CopyToDaysSchemaType) => {
    await mutateAsync(copyToDaysSchemaTypeToPutPlanningBody(room.id, data, userDefinedPlannings));
  };

  return (
    <Card {...props} className={cn("gap-5 p-5 flex h-full w-fit flex-col text-base", className)}>
      <div className="gap-1.25 flex items-center justify-between">
        <Heading as="h2" size={5}>
          {m.planning()}
        </Heading>
        <RoomFrostFreeAlert.Planned result={plannedFrostFree} slots={frostFreeSlots} />
        <div className="gap-2.5 flex items-center text-primary-blue">
          <CanAccess isAuthorized={hasPlanningAccess}>
            <UpdatePlanningDialog
              userDefinedPlannings={userDefinedPlannings}
              types={types}
              onValueChange={handleChangePlanning}
            >
              <Button disabled={isFrostFree}>
                <PlusIcon />
              </Button>
            </UpdatePlanningDialog>
            <CopyPlanningDialog
              onCopy={handleCopyPlanning}
              disabled={isFrostFree}
              open={copyPlanningDialogOpen}
              onOpenChange={setCopyPlanningDialogOpen}
            />
          </CanAccess>
          <OptionsDropdown
            displayUserDefinedPlanning={displayUserDefinedPlanning}
            onDisplayUserDefinedPlanningChange={setDisplayUserDefinedPlanning}
          />
        </div>
      </div>
      <Week.Root className="min-h-0 relative flex-1 overflow-x-auto">
        {reorderedDays.map(day => (
          <Week.Day key={day} day={day as 0 | 1 | 2 | 3 | 4 | 5 | 6}>
            {(timeslotsByDay[day] ?? []).map((timeslot, index) =>
              timeslot.userDefined ? (
                <CanAccess
                  key={index}
                  isAuthorized={hasPlanningAccess}
                  fallback={
                    <Week.Timeslot start={timeslot.hourStart} end={timeslot.hourEnd} type={timeslot.type} userDefined />
                  }
                >
                  <UpdatePlanningDialog
                    userDefinedPlannings={userDefinedPlannings}
                    types={types}
                    value={timeslot}
                    onValueChange={handleChangePlanning}
                  >
                    <Week.Timeslot start={timeslot.hourStart} end={timeslot.hourEnd} type={timeslot.type} userDefined />
                  </UpdatePlanningDialog>
                </CanAccess>
              ) : (
                <Week.Timeslot key={index} start={timeslot.hourStart} end={timeslot.hourEnd} type={timeslot.type} />
              ),
            )}
          </Week.Day>
        ))}
        {isFrostFree && (
          <FrostFreePlanningOverlay
            key={room.id}
            result={frostFreeResult}
            slots={frostFreeSlots}
            heatingNetworks={heatingNetworks}
          />
        )}
      </Week.Root>
      <div className="gap-2 grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))]">
        <PlanningTypeKey type={PlanningType.ABSENCE} temperature={absenceTemperature} />
        <PlanningTypeKey type={PlanningType.COMFORT} temperature={comfortTemperature} />
        <PlanningTypeKey type={PlanningType.NIGHT} temperature={nightTemperature} />
        <PlanningTypeKey type={PlanningType.PRE_COMFORT} temperature={preComfortTemperature} />
        <PlanningTypeKey type="userDefined" />
      </div>
    </Card>
  );
};

PlanningCard.displayName = NAME;

export { PlanningCard };
export type { PlanningCardProps };
